import { useLocation, useNavigation } from 'react-router-dom'

// First path segment, e.g. "/browse/authors" -> "browse". Lets us tell a
// top-level section change (Visualize -> Browse) apart from navigation that
// stays in the same section (a Browse sub-tab) or a same-page revalidation.
function section(pathname: string) {
  return pathname.split('/')[1] ?? ''
}

/**
 * True while React Router is running a blocking navigation toward a different
 * top-level section than the one currently shown.
 *
 * Browse/Visualize/etc. are blocking data routes (route-level `lazy`/`loader`,
 * see app.tsx): the router keeps the current page mounted until the new loaders
 * resolve. A layout uses this to swap in a spinner the moment such a navigation
 * starts, instead of leaving the user on the old page while the header already
 * highlights the new tab.
 *
 * Scoped to section changes so in-section navigation (e.g. Browse sub-tabs) and
 * same-page form revalidations don't blank the whole layout.
 */
export function useIsChangingSection(): boolean {
  const navigation = useNavigation()
  const location = useLocation()

  return (
    navigation.state === 'loading' &&
    navigation.location != null &&
    section(navigation.location.pathname) !== section(location.pathname)
  )
}

/**
 * True while React Router is running a blocking navigation that stays inside
 * the current top-level section -- a Browse sub-tab, or a sort/filter change
 * that re-runs the same loader.
 *
 * The exact complement of useIsChangingSection, and the case it deliberately
 * leaves out: a section change blanks the whole layout (app/layout.tsx), which
 * would be far too heavy for moving between sibling tabs. A section layout uses
 * this instead to mark just its content area as busy while its own chrome --
 * search box, tab nav -- stays put and interactive.
 *
 * Worth having because the router keeps the outgoing tab mounted until the
 * incoming tab's loaders resolve. Where those take a moment (Browse's do), a
 * tab click otherwise produces no visible change at all.
 */
export function useIsLoadingWithinSection(): boolean {
  const navigation = useNavigation()
  const location = useLocation()

  return (
    navigation.state === 'loading' &&
    navigation.location != null &&
    section(navigation.location.pathname) === section(location.pathname)
  )
}
